"use strict";

exports.__esModule = true;
exports.SkinEntry = exports.Skin = void 0;

var _MeshAttachment = require("./attachments/MeshAttachment");

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Stores an entry in the skin consisting of the slot index, name, and attachment **/
var SkinEntry = function SkinEntry(slotIndex, name, attachment) {
  if (slotIndex === void 0) {
    slotIndex = 0;
  }

  if (name === void 0) {
    name = null;
  }

  if (attachment === void 0) {
    attachment = null;
  }

  this.slotIndex = slotIndex;
  this.name = name;
  this.attachment = attachment;
};
/** Stores attachments by slot index and attachment name.
 *
 * See SkeletonData {@link SkeletonData#defaultSkin}, Skeleton {@link Skeleton#skin}, and
 * [Runtime skins](http://esotericsoftware.com/spine-runtime-skins) in the Spine Runtimes Guide. */


exports.SkinEntry = SkinEntry;

var Skin = /*#__PURE__*/function () {
  function Skin(name) {
    /** The skin's name, which is unique across all skins in the skeleton. */
    this.name = null;
    this.attachments = new Array();
    this.bones = Array();
    this.constraints = new Array();
    if (!name) throw new Error("name cannot be null.");
    this.name = name;
  }
  /** Adds an attachment to the skin for the specified slot index and name. */


  var _proto = Skin.prototype;

  _proto.setAttachment = function setAttachment(slotIndex, name, attachment) {
    if (!attachment) throw new Error("attachment cannot be null.");
    var attachments = this.attachments;
    if (slotIndex >= attachments.length) attachments.length = slotIndex + 1;
    if (!attachments[slotIndex]) attachments[slotIndex] = {};
    attachments[slotIndex][name] = attachment;
  }
  /** Adds all attachments, bones, and constraints from the specified skin to this skin. */
  ;

  _proto.addSkin = function addSkin(skin) {
    for (var i = 0; i < skin.bones.length; i++) {
      var bone = skin.bones[i];
      var contained = false;

      for (var ii = 0; ii < this.bones.length; ii++) {
        if (this.bones[ii] == bone) {
          contained = true;
          break;
        }
      }

      if (!contained) this.bones.push(bone);
    }

    for (var _i = 0; _i < skin.constraints.length; _i++) {
      var constraint = skin.constraints[_i];
      var _contained = false;

      for (var _ii = 0; _ii < this.constraints.length; _ii++) {
        if (this.constraints[_ii] == constraint) {
          _contained = true;
          break;
        }
      }

      if (!_contained) this.constraints.push(constraint);
    }

    var attachments = skin.getAttachments();

    for (var _i2 = 0; _i2 < attachments.length; _i2++) {
      var attachment = attachments[_i2];
      this.setAttachment(attachment.slotIndex, attachment.name, attachment.attachment);
    }
  }
  /** Adds all bones and constraints and copies of all attachments from the specified skin to this skin. Mesh attachments are not
   * copied, instead a new linked mesh is created. The attachment copies can be modified without affecting the originals. */
  ;

  _proto.copySkin = function copySkin(skin) {
    for (var i = 0; i < skin.bones.length; i++) {
      var bone = skin.bones[i];
      var contained = false;

      for (var ii = 0; ii < this.bones.length; ii++) {
        if (this.bones[ii] == bone) {
          contained = true;
          break;
        }
      }

      if (!contained) this.bones.push(bone);
    }

    for (var _i3 = 0; _i3 < skin.constraints.length; _i3++) {
      var constraint = skin.constraints[_i3];
      var _contained2 = false;

      for (var _ii2 = 0; _ii2 < this.constraints.length; _ii2++) {
        if (this.constraints[_ii2] == constraint) {
          _contained2 = true;
          break;
        }
      }

      if (!_contained2) this.constraints.push(constraint);
    }

    var attachments = skin.getAttachments();

    for (var _i4 = 0; _i4 < attachments.length; _i4++) {
      var attachment = attachments[_i4];
      if (!attachment.attachment) continue;

      if (attachment.attachment instanceof _MeshAttachment.MeshAttachment) {
        attachment.attachment = attachment.attachment.newLinkedMesh();
        this.setAttachment(attachment.slotIndex, attachment.name, attachment.attachment);
      } else {
        attachment.attachment = attachment.attachment.copy();
        this.setAttachment(attachment.slotIndex, attachment.name, attachment.attachment);
      }
    }
  }
  /** Returns the attachment for the specified slot index and name, or null. */
  ;

  _proto.getAttachment = function getAttachment(slotIndex, name) {
    var dictionary = this.attachments[slotIndex];
    return dictionary ? dictionary[name] : null;
  }
  /** Removes the attachment in the skin for the specified slot index and name, if any. */
  ;

  _proto.removeAttachment = function removeAttachment(slotIndex, name) {
    var dictionary = this.attachments[slotIndex];
    if (dictionary) dictionary[name] = null;
  }
  /** Returns all attachments in this skin. */
  ;

  _proto.getAttachments = function getAttachments() {
    var entries = new Array();

    for (var i = 0; i < this.attachments.length; i++) {
      var slotAttachments = this.attachments[i];

      if (slotAttachments) {
        for (var name in slotAttachments) {
          var attachment = slotAttachments[name];
          if (attachment) entries.push(new SkinEntry(i, name, attachment));
        }
      }
    }

    return entries;
  }
  /** Returns all attachments in this skin for the specified slot index. */
  ;

  _proto.getAttachmentsForSlot = function getAttachmentsForSlot(slotIndex, attachments) {
    var slotAttachments = this.attachments[slotIndex];

    if (slotAttachments) {
      for (var name in slotAttachments) {
        var attachment = slotAttachments[name];
        if (attachment) attachments.push(new SkinEntry(slotIndex, name, attachment));
      }
    }
  }
  /** Clears all attachments, bones, and constraints. */
  ;

  _proto.clear = function clear() {
    this.attachments.length = 0;
    this.bones.length = 0;
    this.constraints.length = 0;
  }
  /** Attach each attachment in this skin if the corresponding attachment in the old skin is currently attached. */
  ;

  _proto.attachAll = function attachAll(skeleton, oldSkin) {
    var slotIndex = 0;

    for (var i = 0; i < skeleton.slots.length; i++) {
      var slot = skeleton.slots[i];
      var slotAttachment = slot.getAttachment();

      if (slotAttachment && slotIndex < oldSkin.attachments.length) {
        var dictionary = oldSkin.attachments[slotIndex];

        for (var key in dictionary) {
          var skinAttachment = dictionary[key];

          if (slotAttachment == skinAttachment) {
            var attachment = this.getAttachment(slotIndex, key);
            if (attachment) slot.setAttachment(attachment);
            break;
          }
        }
      }

      slotIndex++;
    }
  };

  return Skin;
}();

exports.Skin = Skin;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6Ijs7Ozs7QUE4QkE7O0FBOUJBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztBQW9DQTtJQUNhQSxTLEdBQ1osbUJBQW9CQyxTQUFwQixFQUFrREMsSUFBbEQsRUFBOEVDLFVBQTlFLEVBQTJHO0VBQUEsSUFBdkZGLFNBQXVGO0lBQXZGQSxTQUF1RixHQUFuRSxDQUFtRTtFQUFBOztFQUFBLElBQXpEQyxJQUF5RDtJQUF6REEsSUFBeUQsR0FBMUMsSUFBMEM7RUFBQTs7RUFBQSxJQUE3QkMsVUFBNkI7SUFBN0JBLFVBQTZCLEdBQUosSUFBSTtFQUFBOztFQUF2RjtFQUE4QjtFQUE0QjtBQUFrQyxDO0FBR2pIOzs7Ozs7OztJQUlhQyxJO0VBUVosY0FBYUYsSUFBYixFQUF5QjtJQVB6QjtJQUNBLFlBQWUsSUFBZjtJQUVBLG1CQUFjLElBQUlHLEtBQUosRUFBZDtJQUNBLGFBQVFBLEtBQUssRUFBYjtJQUNBLG1CQUFjLElBQUlBLEtBQUosRUFBZDtJQUdDLElBQUksQ0FBQ0gsSUFBTCxFQUFXLE1BQU0sSUFBSUksS0FBSixDQUFVLHNCQUFWLENBQU47SUFDWCxLQUFLSixJQUFMLEdBQVlBLElBQVo7RUFDQTtFQUVEOzs7OztTQUNBSyxhLEdBQUEsdUJBQWVOLFNBQWYsRUFBa0NDLElBQWxDLEVBQWdEQyxVQUFoRCxFQUFzRTtJQUNyRSxJQUFJLENBQUNBLFVBQUwsRUFBaUIsTUFBTSxJQUFJRyxLQUFKLENBQVUsNEJBQVYsQ0FBTjtJQUNqQixJQUFJRSxXQUFXLEdBQUcsS0FBS0EsV0FBdkI7SUFDQSxJQUFJUCxTQUFTLElBQUlPLFdBQVcsQ0FBQ0MsTUFBN0IsRUFBcUNELFdBQVcsQ0FBQ0MsTUFBWixHQUFxQlIsU0FBUyxHQUFHLENBQWpDO0lBQ3JDLElBQUksQ0FBQ08sV0FBVyxDQUFDUCxTQUFELENBQWhCLEVBQTZCTyxXQUFXLENBQUNQLFNBQUQsQ0FBWCxHQUF5QixFQUF6QjtJQUM3Qk8sV0FBVyxDQUFDUCxTQUFELENBQVgsQ0FBdUJDLElBQXZCLElBQStCQyxVQUEvQjtFQUNBO0VBRUQ7OztTQUNBTyxPLEdBQUEsaUJBQVNDLElBQVQsRUFBbUI7SUFDbEIsS0FBSyxJQUFJQyxDQUFDLEdBQUcsQ0FBYixFQUFnQkEsQ0FBQyxHQUFHRCxJQUFJLENBQUNFLEtBQUwsQ0FBV0osTUFBL0IsRUFBdUNHLENBQUMsRUFBeEMsRUFBNEM7TUFDM0MsSUFBSUUsSUFBSSxHQUFHSCxJQUFJLENBQUNFLEtBQUwsQ0FBV0QsQ0FBWCxDQUFYO01BQ0EsSUFBSUcsU0FBUyxHQUFHLEtBQWhCOztNQUNBLEtBQUssSUFBSUMsRUFBRSxHQUFHLENBQWQsRUFBaUJBLEVBQUUsR0FBRyxLQUFLSCxLQUFMLENBQVdKLE1BQWpDLEVBQXlDTyxFQUFFLEVBQTNDLEVBQStDO1FBQzlDLElBQUksS0FBS0gsS0FBTCxDQUFXRyxFQUFYLEtBQWtCRixJQUF0QixFQUE0QjtVQUMzQkMsU0FBUyxHQUFHLElBQVo7VUFDQTtRQUNBO01BQ0Q7O01BQ0QsSUFBSSxDQUFDQSxTQUFMLEVBQWdCLEtBQUtGLEtBQUwsQ0FBV0ksSUFBWCxDQUFnQkgsSUFBaEI7SUFDaEI7O0lBRUQsS0FBSyxJQUFJRixFQUFDLEdBQUcsQ0FBYixFQUFnQkEsRUFBQyxHQUFHRCxJQUFJLENBQUNPLFdBQUwsQ0FBaUJULE1BQXJDLEVBQTZDRyxFQUFDLEVBQTlDLEVBQWtEO01BQ2pELElBQUlPLFVBQVUsR0FBR1IsSUFBSSxDQUFDTyxXQUFMLENBQWlCTixFQUFqQixDQUFqQjtNQUNBLElBQUlHLFVBQVMsR0FBRyxLQUFoQjs7TUFDQSxLQUFLLElBQUlDLEdBQUUsR0FBRyxDQUFkLEVBQWlCQSxHQUFFLEdBQUcsS0FBS0UsV0FBTCxDQUFpQlQsTUFBdkMsRUFBK0NPLEdBQUUsRUFBakQsRUFBcUQ7UUFDcEQsSUFBSSxLQUFLRSxXQUFMLENBQWlCRixHQUFqQixLQUF3QkcsVUFBNUIsRUFBd0M7VUFDdkNKLFVBQVMsR0FBRyxJQUFaO1VBQ0E7UUFDQTtNQUNEOztNQUNELElBQUksQ0FBQ0EsVUFBTCxFQUFnQixLQUFLRyxXQUFMLENBQWlCRCxJQUFqQixDQUFzQkUsVUFBdEI7SUFDaEI7O0lBRUQsSUFBSVgsV0FBVyxHQUFHRyxJQUFJLENBQUNTLGNBQUwsRUFBbEI7O0lBQ0EsS0FBSyxJQUFJUixHQUFDLEdBQUcsQ0FBYixFQUFnQkEsR0FBQyxHQUFHSixXQUFXLENBQUNDLE1BQWhDLEVBQXdDRyxHQUFDLEVBQXpDLEVBQTZDO01BQzVDLElBQUlULFVBQVUsR0FBR0ssV0FBVyxDQUFDSSxHQUFELENBQTVCO01BQ0EsS0FBS0wsYUFBTCxDQUFtQkosVUFBVSxDQUFDRixTQUE5QixFQUF5Q0UsVUFBVSxDQUFDRCxJQUFwRCxFQUEwREMsVUFBVSxDQUFDQSxVQUFyRTtJQUNBO0VBQ0Q7RUFFRDs7OztTQUVBa0IsUSxHQUFBLGtCQUFVVixJQUFWLEVBQW9CO0lBQ25CLEtBQUssSUFBSUMsQ0FBQyxHQUFHLENBQWIsRUFBZ0JBLENBQUMsR0FBR0QsSUFBSSxDQUFDRSxLQUFMLENBQVdKLE1BQS9CLEVBQXVDRyxDQUFDLEVBQXhDLEVBQTRDO01BQzNDLElBQUlFLElBQUksR0FBR0gsSUFBSSxDQUFDRSxLQUFMLENBQVdELENBQVgsQ0FBWDtNQUNBLElBQUlHLFNBQVMsR0FBRyxLQUFoQjs7TUFDQSxLQUFLLElBQUlDLEVBQUUsR0FBRyxDQUFkLEVBQWlCQSxFQUFFLEdBQUcsS0FBS0gsS0FBTCxDQUFXSixNQUFqQyxFQUF5Q08sRUFBRSxFQUEzQyxFQUErQztRQUM5QyxJQUFJLEtBQUtILEtBQUwsQ0FBV0csRUFBWCxLQUFrQkYsSUFBdEIsRUFBNEI7VUFDM0JDLFNBQVMsR0FBRyxJQUFaO1VBQ0E7UUFDQTtNQUNEOztNQUNELElBQUksQ0FBQ0EsU0FBTCxFQUFnQixLQUFLRixLQUFMLENBQVdJLElBQVgsQ0FBZ0JILElBQWhCO0lBQ2hCOztJQUVELEtBQUssSUFBSUYsR0FBQyxHQUFHLENBQWIsRUFBZ0JBLEdBQUMsR0FBR0QsSUFBSSxDQUFDTyxXQUFMLENBQWlCVCxNQUFyQyxFQUE2Q0csR0FBQyxFQUE5QyxFQUFrRDtNQUNqRCxJQUFJTyxVQUFVLEdBQUdSLElBQUksQ0FBQ08sV0FBTCxDQUFpQk4sR0FBakIsQ0FBakI7TUFDQSxJQUFJRyxXQUFTLEdBQUcsS0FBaEI7O01BQ0EsS0FBSyxJQUFJQyxJQUFFLEdBQUcsQ0FBZCxFQUFpQkEsSUFBRSxHQUFHLEtBQUtFLFdBQUwsQ0FBaUJULE1BQXZDLEVBQStDTyxJQUFFLEVBQWpELEVBQXFEO1FBQ3BELElBQUksS0FBS0UsV0FBTCxDQUFpQkYsSUFBakIsS0FBd0JHLFVBQTVCLEVBQXdDO1VBQ3ZDSixXQUFTLEdBQUcsSUFBWjtVQUNBO1FBQ0E7TUFDRDs7TUFDRCxJQUFJLENBQUNBLFdBQUwsRUFBZ0IsS0FBS0csV0FBTCxDQUFpQkQsSUFBakIsQ0FBc0JFLFVBQXRCO0lBQ2hCOztJQUVELElBQUlYLFdBQVcsR0FBR0csSUFBSSxDQUFDUyxjQUFMLEVBQWxCOztJQUNBLEtBQUssSUFBSVIsR0FBQyxHQUFHLENBQWIsRUFBZ0JBLEdBQUMsR0FBR0osV0FBVyxDQUFDQyxNQUFoQyxFQUF3Q0csR0FBQyxFQUF6QyxFQUE2QztNQUM1QyxJQUFJVCxVQUFVLEdBQUdLLFdBQVcsQ0FBQ0ksR0FBRCxDQUE1QjtNQUNBLElBQUksQ0FBQ1QsVUFBVSxDQUFDQSxVQUFoQixFQUE0Qjs7TUFDNUIsSUFBSUEsVUFBVSxDQUFDQSxVQUFYLFlBQWlDbUIsOEJBQXJDLEVBQXFEO1FBQ3BEbkIsVUFBVSxDQUFDQSxVQUFYLEdBQXdCQSxVQUFVLENBQUNBLFVBQVgsQ0FBc0JvQixhQUF0QixFQUF4QjtRQUNBLEtBQUtoQixhQUFMLENBQW1CSixVQUFVLENBQUNGLFNBQTlCLEVBQXlDRSxVQUFVLENBQUNELElBQXBELEVBQTBEQyxVQUFVLENBQUNBLFVBQXJFO01BQ0EsQ0FIRCxNQUdPO1FBQ05BLFVBQVUsQ0FBQ0EsVUFBWCxHQUF3QkEsVUFBVSxDQUFDQSxVQUFYLENBQXNCcUIsSUFBdEIsRUFBeEI7UUFDQSxLQUFLakIsYUFBTCxDQUFtQkosVUFBVSxDQUFDRixTQUE5QixFQUF5Q0UsVUFBVSxDQUFDRCxJQUFwRCxFQUEwREMsVUFBVSxDQUFDQSxVQUFyRTtNQUNBO0lBQ0Q7RUFDRDtFQUVEOzs7U0FDQXNCLGEsR0FBQSx1QkFBZXhCLFNBQWYsRUFBa0NDLElBQWxDLEVBQThDO0lBQzdDLElBQUl3QixVQUFVLEdBQUcsS0FBS2xCLFdBQUwsQ0FBaUJQLFNBQWpCLENBQWpCO0lBQ0EsT0FBT3lCLFVBQVUsR0FBR0EsVUFBVSxDQUFDeEIsSUFBRCxDQUFiLEdBQXNCLElBQXZDO0VBQ0E7RUFFRDs7O1NBQ0F5QixnQixHQUFBLDBCQUFrQjFCLFNBQWxCLEVBQXFDQyxJQUFyQyxFQUFpRDtJQUNoRCxJQUFJd0IsVUFBVSxHQUFHLEtBQUtsQixXQUFMLENBQWlCUCxTQUFqQixDQUFqQjtJQUNBLElBQUl5QixVQUFKLEVBQWdCQSxVQUFVLENBQUN4QixJQUFELENBQVYsR0FBbUIsSUFBbkI7RUFDaEI7RUFFRDs7O1NBQ0FrQixjLEdBQUEsMEJBQWM7SUFDYixJQUFJUSxPQUFPLEdBQUcsSUFBSXZCLEtBQUosRUFBZDs7SUFDQSxLQUFLLElBQUlPLENBQUMsR0FBRyxDQUFiLEVBQWdCQSxDQUFDLEdBQUcsS0FBS0osV0FBTCxDQUFpQkMsTUFBckMsRUFBNkNHLENBQUMsRUFBOUMsRUFBa0Q7TUFDakQsSUFBSWlCLGVBQWUsR0FBRyxLQUFLckIsV0FBTCxDQUFpQkksQ0FBakIsQ0FBdEI7O01BQ0EsSUFBSWlCLGVBQUosRUFBcUI7UUFDcEIsS0FBSyxJQUFJM0IsSUFBVCxJQUFpQjJCLGVBQWpCLEVBQWtDO1VBQ2pDLElBQUkxQixVQUFVLEdBQUcwQixlQUFlLENBQUMzQixJQUFELENBQWhDO1VBQ0EsSUFBSUMsVUFBSixFQUFnQnlCLE9BQU8sQ0FBQ1gsSUFBUixDQUFhLElBQUlqQixTQUFKLENBQWNZLENBQWQsRUFBaUJWLElBQWpCLEVBQXVCQyxVQUF2QixDQUFiO1FBQ2hCO01BQ0Q7SUFDRDs7SUFDRCxPQUFPeUIsT0FBUDtFQUNBO0VBRUQ7OztTQUNBRSxxQixHQUFBLCtCQUF1QjdCLFNBQXZCLEVBQTBDTyxXQUExQyxFQUF1RTtJQUN0RSxJQUFJcUIsZUFBZSxHQUFHLEtBQUtyQixXQUFMLENBQWlCUCxTQUFqQixDQUF0Qjs7SUFDQSxJQUFJNEIsZUFBSixFQUFxQjtNQUNwQixLQUFLLElBQUkzQixJQUFULElBQWlCMkIsZUFBakIsRUFBa0M7UUFDakMsSUFBSTFCLFVBQVUsR0FBRzBCLGVBQWUsQ0FBQzNCLElBQUQsQ0FBaEM7UUFDQSxJQUFJQyxVQUFKLEVBQWdCSyxXQUFXLENBQUNTLElBQVosQ0FBaUIsSUFBSWpCLFNBQUosQ0FBY0MsU0FBZCxFQUF5QkMsSUFBekIsRUFBK0JDLFVBQS9CLENBQWpCO01BQ2hCO0lBQ0Q7RUFDRDtFQUVEOzs7U0FDQTRCLEssR0FBQSxpQkFBSztJQUNKLEtBQUt2QixXQUFMLENBQWlCQyxNQUFqQixHQUEwQixDQUExQjtJQUNBLEtBQUtJLEtBQUwsQ0FBV0osTUFBWCxHQUFvQixDQUFwQjtJQUNBLEtBQUtTLFdBQUwsQ0FBaUJULE1BQWpCLEdBQTBCLENBQTFCO0VBQ0E7RUFFRDs7O1NBQ0F1QixTLEdBQUEsbUJBQVdDLFFBQVgsRUFBK0JDLE9BQS9CLEVBQTRDO0lBQzNDLElBQUlqQyxTQUFTLEdBQUcsQ0FBaEI7O0lBQ0EsS0FBSyxJQUFJVyxDQUFDLEdBQUcsQ0FBYixFQUFnQkEsQ0FBQyxHQUFHcUIsUUFBUSxDQUFDRSxLQUFULENBQWUxQixNQUFuQyxFQUEyQ0csQ0FBQyxFQUE1QyxFQUFnRDtNQUMvQyxJQUFJd0IsSUFBSSxHQUFHSCxRQUFRLENBQUNFLEtBQVQsQ0FBZXZCLENBQWYsQ0FBWDtNQUNBLElBQUl5QixjQUFjLEdBQUdELElBQUksQ0FBQ1gsYUFBTCxFQUFyQjs7TUFDQSxJQUFJWSxjQUFjLElBQUlwQyxTQUFTLEdBQUdpQyxPQUFPLENBQUMxQixXQUFSLENBQW9CQyxNQUF0RCxFQUE4RDtRQUM3RCxJQUFJaUIsVUFBVSxHQUFHUSxPQUFPLENBQUMxQixXQUFSLENBQW9CUCxTQUFwQixDQUFqQjs7UUFDQSxLQUFLLElBQUlxQyxHQUFULElBQWdCWixVQUFoQixFQUE0QjtVQUMzQixJQUFJYSxjQUFjLEdBQWViLFVBQVUsQ0FBQ1ksR0FBRCxDQUEzQzs7VUFDQSxJQUFJRCxjQUFjLElBQUlFLGNBQXRCLEVBQXNDO1lBQ3JDLElBQUlwQyxVQUFVLEdBQUcsS0FBS3NCLGFBQUwsQ0FBbUJ4QixTQUFuQixFQUE4QnFDLEdBQTlCLENBQWpCO1lBQ0EsSUFBSW5DLFVBQUosRUFBZ0JpQyxJQUFJLENBQUM3QixhQUFMLENBQW1CSixVQUFuQjtZQUNoQjtVQUNBO1FBQ0Q7TUFDRDs7TUFDREYsU0FBUztJQUNUO0VBQ0QsQyIsIm5hbWVzIjpbIlNraW5FbnRyeSIsInNsb3RJbmRleCIsIm5hbWUiLCJhdHRhY2htZW50IiwiU2tpbiIsIkFycmF5IiwiRXJyb3IiLCJzZXRBdHRhY2htZW50IiwiYXR0YWNobWVudHMiLCJsZW5ndGgiLCJhZGRTa2luIiwic2tpbiIsImkiLCJib25lcyIsImJvbmUiLCJjb250YWluZWQiLCJpaSIsInB1c2giLCJjb25zdHJhaW50cyIsImNvbnN0cmFpbnQiLCJnZXRBdHRhY2htZW50cyIsImNvcHlTa2luIiwiTWVzaEF0dGFjaG1lbnQiLCJuZXdMaW5rZWRNZXNoIiwiY29weSIsImdldEF0dGFjaG1lbnQiLCJkaWN0aW9uYXJ5IiwicmVtb3ZlQXR0YWNobWVudCIsImVudHJpZXMiLCJzbG90QXR0YWNobWVudHMiLCJnZXRBdHRhY2htZW50c0ZvclNsb3QiLCJjbGVhciIsImF0dGFjaEFsbCIsInNrZWxldG9uIiwib2xkU2tpbiIsInNsb3RzIiwic2xvdCIsInNsb3RBdHRhY2htZW50Iiwia2V5Iiwic2tpbkF0dGFjaG1lbnQiXSwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vc3JjL1NraW4udHMiXSwic291cmNlc0NvbnRlbnQiOltudWxsXX0=